import { domMax, m } from 'framer-motion';
import type { CSSProperties } from 'react';
import { useLocation } from 'react-router';
import type { PlpModule } from 'types/capi/plp';

import FramerMotion from '~/components/FramerMotion';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import AffiliateLinkClickEvent from '~/events/AffiliateLinkClickEvent';
import SplashSlideInteractionEvent from '~/events/SplashSlideInteractionEvent';
import useEnv from '~/hooks/useEnv';
import useImpressions from '~/hooks/useImpressions';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useSplashSlideImpression from '~/hooks/useSplashSlideImpression';
import useUser from '~/hooks/useUser';
import wct from '~/services/wct.client';
import bannerName from '~/utils/bannerName';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

type Props = {
  className: string;
  modules?: PlpModule[];
  slot: 1 | 2 | 3 | 4;
  style?: CSSProperties;
};

export default function ImageTile({ className, modules, slot, style }: Props) {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot, session } = useRootData();
  const { pathname, search } = useLocation();

  const moduleType = `plp-image-${slot}-banner` as const;

  const item = modules?.find((m) => m.type === 'HEADER_CAROUSEL')?.items[0];

  const [bannerRef] = useImpressions([
    {
      bannerId: moduleType,
      bannerName: bannerName(moduleType, item?.heading),
      bannerPosition: 'PLP Image Modules'
    }
  ]);

  const impressionRef = useSplashSlideImpression<HTMLDivElement>({
    moduleType,
    position: 1,
    slide: item
  });

  if (!item) return null;

  const overlay = <Overlay item={item} />;

  const picture = (
    <Image
      alt={item.images.mobile.alt}
      src={item.images.mobile.url}
      display="full-bleed"
      className="size-full object-cover"
      responsiveOptions={{
        md: { src: item.images.desktop.url },
        xs: { src: item.images.mobile.url }
      }}
      progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
    />
  );

  const { cta } = item;
  const name = bannerName(moduleType, item.heading);

  const slideEvent = {
    guestId: user.guestId,
    heading: item.heading,
    imageUrl: item.images.mobile?.url || item.images.desktop.url,
    isBot,
    moduleType,
    position: 1
  };
  const hasVisibleText = !!(item.heading || item.sub_heading || cta?.text);
  const label = hasVisibleText
    ? undefined
    : item.images.desktop.alt || item.images.mobile.alt;

  let content = (
    <span className="absolute inset-0 block">
      {picture}
      {overlay}
    </span>
  );

  if (cta?.type === 'internal') {
    content = (
      <Tracked.InternalLink
        aria-label={label}
        className="absolute inset-0 block"
        name={name}
        onClick={() =>
          new SplashSlideInteractionEvent({
            ...slideEvent,
            destinationPath: cta.path,
            gesture: 'click'
          }).send()
        }
        pagePosition="PLP Image Modules"
        to={cta.path}
        variant="none"
      >
        {picture}
        {overlay}
      </Tracked.InternalLink>
    );
  }

  if (cta?.type === 'external') {
    const { url, clickRef } = getTrackedUrlAndClickRef(cta, { user });

    const onClick = () => {
      new SplashSlideInteractionEvent({
        ...slideEvent,
        clickref: clickRef,
        destinationUrl: cta.url,
        gesture: 'click',
        retailerId: cta.affiliate?.canonical_id
      }).send();

      wct.fireEvent({
        clickRef,
        clientBrowser,
        clientIp,
        landingPage: marketingCookies.getLandingPage() ?? 'unknown',
        pagePath: `${env.SITE_BASE_URL}${pathname}${search}`,
        session,
        url
      });

      if (cta.affiliate)
        new AffiliateLinkClickEvent({
          affiliateName: cta.affiliate.name,
          clientIp,
          isBot,
          isReseller: cta.affiliate.is_reseller,
          pagePosition: 'PLP Image Modules',
          retailerId: cta.affiliate.id,
          user
        }).send();
    };

    content = (
      <Tracked.ExternalLink
        aria-label={label}
        className="absolute inset-0 block"
        href={url}
        onClick={onClick}
        name={name}
        pagePosition="PLP Image Modules"
        target="_blank"
        variant="none"
      >
        {picture}
        {overlay}
      </Tracked.ExternalLink>
    );
  }

  return (
    <FramerMotion feature={domMax}>
      <m.div
        className={className}
        layout
        ref={(element: HTMLDivElement | null) => {
          bannerRef?.(element);
          impressionRef.current = element;
        }}
        style={style}
      >
        {content}
      </m.div>
    </FramerMotion>
  );
}

type Item = NonNullable<
  Extract<PlpModule, { type: 'HEADER_CAROUSEL' }>['items']
>[0];

// The gradient is what makes white text legible over a photograph, so it only
// earns its place when there is text to carry.
function Overlay({ item }: { item: Item }) {
  const ctaText = item.cta?.text;

  if (!item.heading && !item.sub_heading && !ctaText) return null;

  return (
    <span className="absolute inset-x-0 bottom-0 block">
      <span className="pointer-events-none absolute inset-x-0 -top-20 bottom-0 block bg-gradient-to-b from-transparent to-black/[0.72] opacity-80" />
      <span className="relative flex items-end gap-4 px-6 pb-6 text-white md:px-8 md:pb-8">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {item.sub_heading && (
            <span className="line-clamp-3 text-xs leading-5 tracking-[0.24px]">
              {item.sub_heading}
            </span>
          )}
          {item.heading && (
            <span className="text-card-md line-clamp-3 tracking-[-0.96px]">
              {item.heading}
            </span>
          )}
        </span>
        {ctaText && (
          <span className="flex h-8 shrink-0 items-center justify-center whitespace-nowrap rounded-lg border border-white px-4 py-2 text-center font-heading text-sm font-semibold leading-6 tracking-[-0.28px]">
            {ctaText}
          </span>
        )}
      </span>
    </span>
  );
}
